import { PAYMENT_GATEWAY, SUBSCRIPTION_STATUS } from '@adtrackify/at-tracking-event-types';
import { AlertTriangle, ArrowRight, Mail, Calendar } from 'lucide-react';
import { type ReactNode, lazy, Suspense, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { MainContentLoading } from '@/components/ui/main-content-loading';
import { useSubscriptionGuard } from '@/features/billing/hooks/use-subscription-guard';
import { ACCOUNT_ACCESS_MODE, ACCOUNT_ACCESS_REASON } from '@/features/billing/types/use-subscription-guard-enums';
import { cn } from '@/lib/utils';
import { invalidateUserData } from '@/lib/query/cache-invalidation';
import { useBillingPaymentMethods } from '@/features/billing/hooks/use-billing-payment-methods';
import { useAccountState } from '@/contexts/account/account-context';
import {
  BILLING_DESTINATION_LEGACY,
  BILLING_DESTINATION_SHOPIFY,
  SETTINGS_INVOICES_PATH,
  SETTINGS_PAYMENT_METHODS_PATH,
  SETTINGS_PLANS_PATH,
  SETTINGS_USAGE_PATH,
  STRIPE_PLAN_SELECTION_PATH,
} from '@/lib/navigation/constants/billing-destination-constants';
import type { CompleteStripeSetupIntentResponse } from '@/services/internal-api/subscriptions/types/subscription-api-service-types';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from '@/components/ui/helpers/toast';

async function loadAddPaymentMethodModal() {
  const module = await import('@/features/billing/components/payment-methods/add-payment-method-modal');
  return { default: module.AddPaymentMethodModal };
}

const AddPaymentMethodModal = lazy(loadAddPaymentMethodModal);

import { RECOVERY_INVOICE_RETRY_MODAL_PROPS } from '@/features/billing/constants/billing-recovery-constants';

interface AccessGuardProps {
  children: ReactNode;
  className?: string;
}

// The whole Billing area (overview, usage, payment methods, invoices, plans) must
// stay reachable for every account state — cancelled, expired, pending, suspended —
// so customers can always see old invoices and manage their card and subscription.
const EXEMPTED_ROUTES = [
  '/onboarding',
  '/get-started',
  STRIPE_PLAN_SELECTION_PATH,
  '/account/shopify-plan',
  BILLING_DESTINATION_LEGACY,
  SETTINGS_USAGE_PATH,
  SETTINGS_PAYMENT_METHODS_PATH,
  SETTINGS_INVOICES_PATH,
  SETTINGS_PLANS_PATH,
  BILLING_DESTINATION_SHOPIFY,
  '/account/billing-recovery',
  '/shopify/subscription-updated',
  '/payment',
  '/signup',
];

function getReasonCopy(reason?: ACCOUNT_ACCESS_REASON): { title: string; message: string; ctaLabel?: string } {
  switch (reason) {
    case ACCOUNT_ACCESS_REASON.SUBSCRIPTION_MISSING:
      return {
        title: 'Choose a Plan',
        message: 'Select a subscription plan to get started with your dashboards and tools.',
        ctaLabel: 'Select Plan',
      };
    case ACCOUNT_ACCESS_REASON.SUBSCRIPTION_CANCELLED:
      return {
        title: 'Subscription Cancelled',
        message: 'Your subscription has been cancelled. Please select a plan to regain access to your dashboards and tools.',
      };
    case ACCOUNT_ACCESS_REASON.BILLING_SUSPENDED_NONPAYMENT:
      return {
        title: 'Account Suspended',
        message: 'Your account has been suspended due to a billing issue. Please update your payment method to restore access.',
      };
    case ACCOUNT_ACCESS_REASON.SUBSCRIPTION_PAUSED:
      return {
        title: 'Subscription Paused',
        message: 'Your subscription is currently paused. Please contact support or resume your subscription to restore access.',
      };
    case ACCOUNT_ACCESS_REASON.ACCOUNT_DISABLED_ADMIN:
      return {
        title: 'Account Disabled',
        message: 'Your account has been disabled. Please contact support for assistance.',
      };
    case ACCOUNT_ACCESS_REASON.SHOPIFY_BILLING_ISSUE:
      return {
        title: 'Billing Issue',
        message: 'There is a billing issue with your Shopify subscription. Please manage your subscription in the Shopify admin.',
      };
    case ACCOUNT_ACCESS_REASON.IN_RECOVERY:
      return {
        title: 'Payment Required',
        message: 'Your subscription requires a payment method. Please add a payment method to activate your plan.',
      };
    default:
      return {
        title: 'Access Restricted',
        message: 'Please select a subscription plan to access your dashboards and tools.',
      };
  }
}

function RestrictedAccessState({ reason, onSelectPlan, onAddPayment }: { reason?: ACCOUNT_ACCESS_REASON; onSelectPlan: () => void; onAddPayment?: () => void }) {
  const { title, message, ctaLabel } = getReasonCopy(reason);
  const isPendingPayment = reason === ACCOUNT_ACCESS_REASON.IN_RECOVERY;

  function getButtonLabel(): string {
    if (isPendingPayment) return 'Add Payment Method';
    if (reason === ACCOUNT_ACCESS_REASON.BILLING_SUSPENDED_NONPAYMENT) return 'Pay Outstanding Balance';
    if (ctaLabel) return ctaLabel;
    return 'Manage Billing';
  }

  return (
    <div className="mx-4 max-w-md rounded-xl border border-border bg-background p-8 text-center shadow-card-elevated">
      <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-status-warning/10">
        <AlertTriangle className="h-7 w-7 text-status-warning" />
      </div>
      <h3 className="mb-2 text-xl font-semibold text-foreground">{title}</h3>
      <p className="mb-6 text-sm text-foreground/90">{message}</p>

      <div className="flex flex-col gap-3">
        <Button onClick={isPendingPayment && onAddPayment ? onAddPayment : onSelectPlan} className="w-full gap-2">
          {getButtonLabel()}
          <ArrowRight className="h-4 w-4" />
        </Button>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="flex-1 gap-1" asChild>
            <a href="mailto:support@upstackdata.com">
              <Mail className="h-3.5 w-3.5" />
              Email Support
            </a>
          </Button>
          <Button variant="outline" size="sm" className="flex-1 gap-1" asChild>
            <a href="https://cal.com/michael-upstack/15m-support-call-upstack-data?overlayCalendar=true" target="_blank" rel="noopener noreferrer">
              <Calendar className="h-3.5 w-3.5" />
              Schedule a Call
            </a>
          </Button>
        </div>
      </div>
    </div>
  );
}

export function AccessGuard({ children, className }: AccessGuardProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { currentAccount, hasLoadedAccounts } = useAccountState();
  const { mode, reason, isLoading } = useSubscriptionGuard();
  const loggedRef = useRef<string | null>(null);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);

  const subscription = currentAccount?.subscription;
  const paymentGateway = subscription?.paymentGateway;
  const isPending = subscription?.status === 'pending';

  const isStripeActive = paymentGateway === PAYMENT_GATEWAY.STRIPE
    && mode === ACCOUNT_ACCESS_MODE.FULL_ACCESS;
  const isExemptedRoute = EXEMPTED_ROUTES.some(route =>
    location.pathname.startsWith(route)
  );
  // Subscribe to the PM cache (populated by PaymentMethodsBootstrap) without
  // firing a fetch. Bootstrap is the sole writer; we re-render whenever it
  // resolves so the recovery state appears mid-navigation. While the cache is
  // undefined we render children optimistically — the gate only engages once
  // we know (cache populated) the user has zero PMs.
  // Wait until accounts have loaded so a stale empty PM cache cannot flash
  // "Payment Required" / Manage Billing during optimistic warm boot.
  const { data: cachedPaymentMethods } = useBillingPaymentMethods({ enabled: false });
  // Cardless trials intentionally have no payment method; do not force recovery UI.
  // However, this exemption only applies when the subscription is ACTIVE or TRIALING.
  // If a cardless trial ends and transitions to PAST_DUE/UNPAID, recovery UI must engage.
  const isActiveCardlessTrial = subscription?.isCardlessTrial === true
    && (subscription?.status === SUBSCRIPTION_STATUS.ACTIVE
      || subscription?.status === SUBSCRIPTION_STATUS.TRIALING);
  const hasNoPaymentMethod = hasLoadedAccounts
    && isStripeActive
    && !isActiveCardlessTrial
    && cachedPaymentMethods !== undefined
    && cachedPaymentMethods.length === 0;

  useEffect(() => {
    if (mode === ACCOUNT_ACCESS_MODE.BILLING_ONLY && reason) {
      const key = `${reason}:${location.pathname}`;
      if (loggedRef.current !== key) {
        loggedRef.current = key;
        console.info('[AccessGuard] Account access gated', {
          accountId: currentAccount?.id,
          mode,
          reason,
          pathname: location.pathname,
        });
      }
    }
  }, [mode, reason, location.pathname, currentAccount?.id]);

  const handleSelectPlan = () => {
    // DISABLED + NONPAYMENT: route to billing recovery, not plan selection
    if (reason === ACCOUNT_ACCESS_REASON.BILLING_SUSPENDED_NONPAYMENT) {
      void navigate('/account/billing-recovery');
      return;
    }

    const isCancelledOrExpired =
      subscription?.status === 'cancelled' || subscription?.status === 'expired';

    if (isCancelledOrExpired) {
      void navigate('/account/stripe-plan');
      return;
    }

    const route = paymentGateway === 'shopify' ? '/account/shopify-plan' : '/account/stripe-plan';
    void navigate(route);
  };

  const handlePaymentSuccess = (response: CompleteStripeSetupIntentResponse) => {
    invalidateUserData(queryClient);
    if (response.subscriptionActivated) {
      toast.success('Payment method added and subscription activated!');
    } else {
      toast.success('Payment method added successfully.');
    }
    setIsPaymentModalOpen(false);
  };

  if (isExemptedRoute) {
    return <>{children}</>;
  }

  // Loading OR mode not yet classified — never fall through to RestrictedAccessState
  // (that path defaults to "Access Restricted" / Manage Billing and flickers on
  // optimistic Cognito shell when token/mode are briefly unset).
  if (isLoading || mode === undefined) {
    if (!currentAccount) {
      return (
        <MainContentLoading
          className={className}
          label="Checking account access…"
        />
      );
    }

    return <>{children}</>;
  }

  if (mode === ACCOUNT_ACCESS_MODE.FULL_ACCESS && !hasNoPaymentMethod) {
    return <>{children}</>;
  }

  // BILLING_ONLY (or FULL_ACCESS with confirmed empty PMs after accounts loaded)
  const effectiveReason = hasNoPaymentMethod
    ? ACCOUNT_ACCESS_REASON.IN_RECOVERY
    : reason;

  const isRecoveryFlow = reason === ACCOUNT_ACCESS_REASON.BILLING_SUSPENDED_NONPAYMENT;
  const showPaymentModal = isPending || hasNoPaymentMethod || isRecoveryFlow;

  return (
    <>
      <div className={cn('flex h-[calc(100vh-180px)] items-center justify-center', className)}>
        <RestrictedAccessState
          reason={effectiveReason}
          onSelectPlan={handleSelectPlan}
          onAddPayment={() => setIsPaymentModalOpen(true)}
        />
      </div>
      {showPaymentModal && (
        <Suspense fallback={null}>
          <AddPaymentMethodModal
            open={isPaymentModalOpen}
            onOpenChange={setIsPaymentModalOpen}
            onSuccess={handlePaymentSuccess}
            makeDefaultPaymentMethod
            analyticsFlow={isRecoveryFlow ? 'recovery' : 'activation'}
            {...(isRecoveryFlow ? RECOVERY_INVOICE_RETRY_MODAL_PROPS : {})}
          />
        </Suspense>
      )}
    </>
  );
}
